import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import type { CityHallStateResponse } from "../../client";
import { SHELL_ICON } from "./constants";


interface ElectionsUnavailableContentProps {
  formatDate: (dateString: string | null | undefined) => string;
  formatNextElectionTimeRemaining: (nextElectionStartDate: string | null | undefined) => string;
  setCurrentScreen: (screen: "main") => void;
  state: CityHallStateResponse | undefined;
  t: TFunction;
}

function ElectionsUnavailableContent({
  formatDate,
  formatNextElectionTimeRemaining,
  setCurrentScreen,
  state,
  t,
}: ElectionsUnavailableContentProps) {
  return (
    <>
      <div className="flex flex-col items-start self-stretch whitespace-pre-wrap rounded-xl border border-brand bg-white p-3xl">
        <div
          className="mb-lg [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
        >
          {t("Elder")}
        </div>
        <div
          className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
        >
          {t("The Mayor has already been recently elected, so Mayor elections are not currently being held!")}
          {state?.next_election_start_date && (() => {
            const timeRemaining = formatNextElectionTimeRemaining(state.next_election_start_date);
            return (
              <>
                <br />
                <br />
                {t("Date of the next Mayor election start:")}
                <br />
                <span className="font-bold">
                  {formatDate(state.next_election_start_date)}
                </span>
                {timeRemaining && (
                  <>
                    <br />
                    <span className="text-[14px] opacity-90">
                      ({timeRemaining})
                    </span>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Back button */}
      <div className="mt-lg flex justify-end">
        <button
          onClick={() => setCurrentScreen("main")}
          className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
        >
          {t("Back")}
        </button>
      </div>
    </>
  );
}

interface RegisterScreenProps {
  currentUserCandidateId: number | null;
  error: string | null;
  formatDate: (dateString: string | null | undefined) => string;
  formatNextElectionTimeRemaining: (nextElectionStartDate: string | null | undefined) => string;
  handleRegister: () => void;
  isLoading: boolean;
  setCurrentScreen: (screen: "main") => void;
  state: CityHallStateResponse | undefined;
  t: TFunction;
}

export function RegisterScreen({
  currentUserCandidateId,
  error,
  formatDate,
  formatNextElectionTimeRemaining,
  handleRegister,
  isLoading,
  setCurrentScreen,
  state,
  t,
}: RegisterScreenProps) {
  return !state?.voting_available && state?.has_mayor ? (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <ElectionsUnavailableContent
            formatDate={formatDate}
            formatNextElectionTimeRemaining={formatNextElectionTimeRemaining}
            setCurrentScreen={setCurrentScreen}
            state={state}
            t={t}
          />
        </div>
      ) : state && (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <div
            className="flex flex-col items-end gap-[21px]"
          >
            <div className="flex flex-col items-start self-stretch whitespace-pre-wrap rounded-xl border border-brand bg-white p-3xl">
              <div
                className="mb-0 [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
              >
                {t("Elder")}
              </div>
              <div
                className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
              >
                {currentUserCandidateId ? (
                  <>
                    <div className="mb-lg">
                      {t("You are already registered as a candidate!")}
                    </div>
                    <div className="mb-lg">
                      {t("Your candidate number")}: {currentUserCandidateId}
                    </div>
                    <div>
                      {t("To become the Mayor, you must receive more votes than all other candidates.")}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-lg">
                      {t("To register as a candidate, you must pay:")}{" "}
                      <span className="inline-flex items-center gap-xs align-middle">
                        {state.registration_price_pearls.toLocaleString("en-US")}
                        <img src={SHELL_ICON} alt="" className="h-xl w-2xl" />
                      </span>
                      .
                    </div>
                    <div className="mb-lg">
                      {t("This payment is only for registration and does not give you any votes. To become the Mayor, you must receive more votes than all other candidates.")}
                    </div>
                    <div>
                      {t("If you lose the election, your registration payment will not be refunded. All payments are final.")}
                    </div>
                  </>
                )}
              </div>
            </div>

            {state?.registration_available && !currentUserCandidateId && (
              <div className="flex flex-col items-end gap-lg self-stretch">
                <button
                  onClick={() => handleRegister()}
                  disabled={isLoading || !state?.registration_available}
                  className="flex w-fit items-center justify-center gap-sm rounded-pill border-none bg-white px-3xl py-xl"
                  style={{
                    cursor: isLoading || !state?.registration_available ? "not-allowed" : "pointer",
                    opacity: isLoading || !state?.registration_available ? 0.6 : 1,
                  }}
                >
                  <div
                    className="flex items-center gap-sm [font-family:'Rubik_One',sans-serif] text-[14px] leading-[29px] font-normal text-text-dark"
                  >
                    {state.registration_price_pearls.toLocaleString("en-US")}
                    <img
                      src={SHELL_ICON}
                      alt={t("Pearl")}
                      className="h-[25.335px] w-[32.02px]"
                    />
                  </div>
                </button>
            </div>
            )}
          </div>

          {error && (
            <div
              className="rounded-sm border border-register-screens-border bg-register-screens-surface p-lg text-[14px] text-error-soft"
            >
              {error}
            </div>
          )}

          {/* Back button */}
          <div className="mt-lg flex justify-end">
            <button
              onClick={() => setCurrentScreen("main")}
              className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
            >
              {t("Back")}
            </button>
          </div>
        </div>
      );
}

interface RegisterSuccessScreenProps {
  formatDate: (dateString: string | null | undefined) => string;
  registerSuccessDisplayCandidateId: number | null;
  setCurrentScreen: (screen: "main") => void;
  setRegisterSuccessCandidateId: Dispatch<SetStateAction<number | null>>;
  state: CityHallStateResponse | undefined;
  t: TFunction;
}

export function RegisterSuccessScreen({
  formatDate,
  registerSuccessDisplayCandidateId,
  setCurrentScreen,
  setRegisterSuccessCandidateId,
  state,
  t,
}: RegisterSuccessScreenProps) {
  return (
        <div
          className="cityhall-scrollable absolute top-0 left-1/2 bottom-0 z-[10004] flex w-full max-w-[332px] -translate-x-1/2 flex-col gap-3xl overflow-x-hidden overflow-y-auto pt-[54px] pb-2xl"
        >
          <div className="flex flex-col items-end">
            <div className="flex flex-col items-start self-stretch whitespace-pre-wrap rounded-xl border border-brand bg-white p-3xl">
              <div
                className="mb-0 [font-family:'Rubik_One',sans-serif] text-[20px] leading-[40px] font-normal text-deep-blue"
              >
                {t("Elder")}
              </div>
              <div
                className="[font-family:Lato,sans-serif] text-[18px] leading-[30px] font-normal text-text-dark"
              >
                <div className="mb-lg">
                  {t("Your registration is confirmed")}
                </div>
                {registerSuccessDisplayCandidateId && (
                  <div className="mb-lg">
                    {t("Your candidate number")}: {registerSuccessDisplayCandidateId}
                  </div>
                )}
                {state?.next_election_start_date && (
                  <div className="mb-lg">
                    {t("Election starts on")}: {formatDate(state.next_election_start_date)}
                  </div>
                )}
                {state?.election_end_at && (
                  <div>
                    {t("Election ends on")}: {formatDate(state.election_end_at)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-lg flex justify-end">
            <button
              onClick={() => {
                setRegisterSuccessCandidateId(null);
                setCurrentScreen("main");
              }}
              className="flex w-fit cursor-pointer items-center justify-center gap-sm rounded-pill border-none bg-[image:var(--gradient-action-primary)] px-[48px] py-lg [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal text-white shadow-action-raised"
            >
              {t("Back")}
            </button>
          </div>
        </div>
  );
}
